import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import kebabCase from 'lodash/kebabCase';
import * as R from 'ramda';

/**
 * Transforms the keys of an object recursively
 * @source https://github.com/ramda/ramda/wiki/Cookbook#camelizekeys
 */
const transformObjectKeysDeep = R.curry(
  (
    transform: (s: string) => string,
    obj: Record<string, unknown>,
  ): Record<string, unknown> =>
    // @ts-ignore
    R.cond([
      [
        R.is(Array),
        R.map(transformObjectKeysDeep(transform) as (obj: unknown) => unknown),
      ],
      // Don't muck with functions
      [R.is(Function), R.identity],
      // Don't muck with rich objects
      [R.is(Date), R.identity],
      [R.is(RegExp), R.identity],
      [R.is(Error), R.identity],
      [R.is(Map), R.identity],
      [R.is(Set), R.identity],
      [R.is(WeakMap), R.identity],
      [R.is(WeakSet), R.identity],

      [
        R.is(Object),
        // @ts-ignore
        R.pipe(
          R.toPairs,
          // @ts-ignore Typedef issue in lib: tests prove this works
          R.map(
            R.juxt([
              R.o(transform, R.head),
              // @ts-expect-error
              R.o(transformObjectKeysDeep(transform), R.last),
            ]),
          ),
          R.fromPairs,
        ),
      ],
      [R.T, R.identity],
      // @ts-ignore
    ])(obj) as Record<string, unknown>,
);

/**
 * Camelcases the keys of an object only one level deep
 */
export const objectKeysToCamelCase = (
  obj: Record<string, unknown>,
): Record<string, unknown> =>
  Object.entries(obj).reduce(
    (acc, [k, v]) => ({
      ...acc,
      [camelCase(k)]: v,
    }),
    {},
  );

/**
 * Camelcases the keys of an object recursively
 */
export const objectKeysToCamelCaseDeep: typeof objectKeysToCamelCase =
  transformObjectKeysDeep(camelCase);

export const objectKeysToSnakeCase = (
  obj: Record<string, unknown>,
): Record<string, unknown> =>
  Object.entries(obj).reduce(
    (acc, [k, v]) => ({
      ...acc,
      [snakeCase(k)]: v,
    }),
    {},
  );

/**
 * Snakecases the keys of an object recursively
 */
export const objectKeysToSnakeCaseDeep: typeof objectKeysToSnakeCase =
  transformObjectKeysDeep(snakeCase);

export const objectKeysToKebabCase = (
  obj: Record<string, unknown>,
): Record<string, unknown> =>
  Object.entries(obj).reduce(
    (acc, [k, v]) => ({
      ...acc,
      [kebabCase(k)]: v,
    }),
    {},
  );

export const objectKeysToKebabCaseDeep: typeof objectKeysToKebabCase =
  transformObjectKeysDeep(kebabCase);

/**
 * Drop keys in an object that are either null, undefined, or empty
 */
export const dropNilOrEmptyValues = R.reject(R.either(R.isEmpty, R.isNil));

/**
 * Renames an object key
 *
 * @see https://github.com/ramda/ramda/wiki/Cookbook#rename-key-of-an-object
 */
export const renameKey = R.curry(
  (oldKey: string, newKey: string, obj: Record<string, any>) =>
    R.assoc(newKey, R.prop(oldKey, obj), R.dissoc(oldKey, obj)),
);

/** Rename multiple keys in an object. Keymap is in format [oldKey, newKey] */
export const renameKeys = R.curry(
  (keymap: [string, string][], obj: Record<string, any>) => {
    const renameFns = keymap.map(R.apply(renameKey));
    // @ts-ignore
    return R.pipe(...renameFns)(obj);
  },
);

/** Returns a partial version of T that contains only the fields that vary between the two */
export const getObjectDiff = <T extends Record<string, unknown>>(
  newObj: T,
  oldObj: T,
): Partial<T> =>
  R.pickBy((value, key) => !R.equals(value, oldObj[key]), newObj);
